/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useLabels } from '@/hooks/useLabels';
import { Text, View, Icon, Actionable, Divider } from '@az/starc-ui';
import { ThumbsUp, ThumbsDown, Checkmark } from '@az/starc-ui-icons';
import cx from 'classnames';
import { useSubmitFeedback } from '../../hooks/useSubmitFeedback';
import styles from './styles.module.scss';

export type ReviewFeedbackActionsProps = {
  reviewId: string;
  negativeFeedbackCount: number;
  positiveFeedbackCount: number;
  questionClassName?: string;
};

const labelMap = {
  wasReviewHelpful: 'label_reviews_wasReviewHelpful',
  helpful: 'label_reviews_helpful',
  notHelpful: 'label_reviews_notHelpful',
  report: 'label_reviews_report',
  reported: 'label_reviews_reported',
};

export const ReviewFeedbackActions = ({
  reviewId,
  positiveFeedbackCount,
  negativeFeedbackCount,
  questionClassName,
}: ReviewFeedbackActionsProps) => {
  const labels = useLabels(labelMap);
  const { submitFeedback, localFeedback } = useSubmitFeedback();

  const helpfulness = localFeedback?.helpfulness[reviewId];

  const reported = localFeedback?.inappropriate[reviewId];
  const disabled = Boolean(helpfulness);
  const isPositiveFeedback = helpfulness === 'positive';
  const isNegativeFeedback = helpfulness === 'negative';

  return (
    <View className={styles.feedbackRow}>
      <Text
        as="p"
        attributes={{ 'data-testid': 'feedback' }}
        className={cx(styles.feedbackQuestion, questionClassName)}
        variant="small-body"
      >
        <strong>{labels.wasReviewHelpful}</strong>
      </Text>
      <View className={styles.feedbackButtonsContainer}>
        <View className={styles.thumbsContainer}>
          <View.Item>
            <Actionable onClick={() => submitFeedback(reviewId, 'positive')} disabled={disabled}>
              <Text className="sr-only">{`${labels.helpful} (${positiveFeedbackCount})`}</Text>
              <Icon svg={ThumbsUp} size={{ s: 6, m: 7 }} />
              <Text attributes={{ 'aria-hidden': true }} variant="small-body">{`(${
                isPositiveFeedback ? positiveFeedbackCount + 1 : positiveFeedbackCount
              })`}</Text>
            </Actionable>
          </View.Item>
          <View.Item>
            <Actionable onClick={() => submitFeedback(reviewId, 'negative')} disabled={disabled}>
              <Text className="sr-only">{`${labels.notHelpful} (${negativeFeedbackCount})`}</Text>
              <Icon svg={ThumbsDown} size={{ s: 6, m: 7 }} />
              <Text attributes={{ 'aria-hidden': true }} variant="small-body">{`(${
                isNegativeFeedback ? negativeFeedbackCount + 1 : negativeFeedbackCount
              })`}</Text>
            </Actionable>
          </View.Item>
        </View>
        <View.Item>
          <Divider vertical />
        </View.Item>
        <View.Item>
          <Actionable
            className={styles.report}
            onClick={() => submitFeedback(reviewId, 'report')}
            disabled={reported}
          >
            <Icon svg={Checkmark} size={{ s: 6, m: 7 }} color="400" />
            <Text variant="text-link">{reported ? labels.reported : labels.report}</Text>
          </Actionable>
        </View.Item>
      </View>
    </View>
  );
};
